import React from "react";
import { CheckCircle2, ExternalLink, ArrowRight, ShieldAlert } from "lucide-react";
import Reveal from "./Reveal";
import { useT } from "./i18n";

function Pillar({ p, i }) {
  const clickable = !!p.cta;
  const Wrapper = clickable ? "a" : "div";
  const wprops = clickable
    ? { href: p.cta.url, target: "_blank", rel: "noopener noreferrer", "data-testid": `eco-pillar-${p.tag.toLowerCase()}-link` }
    : { "data-testid": `eco-pillar-${p.tag.toLowerCase()}` };
  return (
    <Reveal delay={(i % 3) + 1} className="h-full">
      <Wrapper {...wprops}
        className={`flex h-full flex-col rounded-2xl border border-[#E8E4DB] bg-white p-6 card-lift ${clickable ? "cursor-pointer hover:border-[#C4A46B]" : ""}`}>
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1F4A38] text-sm font-bold text-[#C4A46B]">{p.tag}</span>
          <div>
            <h3 className="font-serif text-xl leading-tight text-[#173A2C]">{p.name}</h3>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C4A46B]">{p.role}</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#5C5855]">{p.desc}</p>
        <ul className="mt-4 space-y-1.5">
          {p.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-sm text-[#2C2A29]">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1F4A38]" /> {it}
            </li>
          ))}
        </ul>
        {p.status && (
          <span data-testid="eco-partner-status" className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-[#C4A46B] bg-[#C4A46B]/10 px-3 py-1 text-xs font-medium text-[#8a6f38]">
            {p.status}
          </span>
        )}
        {clickable && (
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F4A38]">
            {p.cta.label} <ExternalLink className="h-3.5 w-3.5" />
          </span>
        )}
      </Wrapper>
    </Reveal>
  );
}

export default function Ecosystem() {
  const { t } = useT();
  const e = t.eco;
  return (
    <section id="ecosystem" className="scroll-mt-24 bg-[#FDFBF7] py-20" data-testid="ecosystem-section">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal className="max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C4A46B]">{e.eyebrow}</p>
          <h2 className="mt-3 font-serif text-3xl leading-tight text-[#173A2C] sm:text-4xl lg:text-[2.7rem]">{e.heading}</h2>
          <p className="mt-4 text-base leading-relaxed text-[#5C5855]">{e.subtitle}</p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {e.pillars.map((p, i) => <Pillar key={p.tag} p={p} i={i} />)}
        </div>

        {/* Connected candidate journey */}
        <Reveal className="mt-16 rounded-3xl border border-[#E8E4DB] bg-white p-8">
          <h3 className="font-serif text-2xl text-[#173A2C]">{e.journeyTitle}</h3>
          <ol className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {e.journey.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#1F4A38]/8 text-xs font-bold text-[#1F4A38]">{i + 1}</span>
                <p className="text-sm leading-snug text-[#2C2A29]">{step}</p>
              </li>
            ))}
          </ol>
          <div className="mt-7 border-t border-[#F0ECE3] pt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#8a8580]">{e.caseLabel}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {e.caseItems.map((c) => (
                <span key={c} className="rounded-full border border-dashed border-[#C4A46B] px-3 py-1 text-xs text-[#8a6f38]">{c}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Disclaimer */}
        <Reveal className="mt-8 flex gap-3 rounded-2xl bg-[#173A2C] p-6 text-white/85" data-testid="eco-disclaimer">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#C4A46B]" />
          <p className="text-xs leading-relaxed">{e.disclaimer}</p>
        </Reveal>
      </div>
    </section>
  );
}
